import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import styled from 'styled-components/macro';
import Post from './Post';
import Button from '../../shared/Button';
import { isFetched } from '../../../store/appState';
import * as Actions from '../../../store/actions';
import { ResourceStrings } from '../../../ResourceStrings';
import Flex from '../../shared/Flex';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { stringFromErrorOrAny } from '../../../utils/tools';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { ModerationListFilter } from '../../../Moderation';
import RefreshButton from '../MyFeedback/RefreshButton';
import { formatNumber } from 'humanize-plus';

const pageSize: number = 5;

export default function ModerationList(): JSX.Element {
    const moderationPostsResult = useSelector((state: RootState) => state.appState.moderationPosts);
    const [pageNumber, setPageNumber] = useState(0);
    const previousPageNumber = useRef<number>();    // To ensure scrollIntoView below isn't called on first page load
    const signinState = useSelector((state: RootState) => state.appState.signInState);
    const [moderationListFilter, setModerationListFilter] = useState(ModerationListFilter.postsAwaitingModeration);
    const [refreshedAt, setRefreshedAt] = useState<number>(null);   // used for forcing a refresh when Refresh button is clicked

    useEffect(() => {
        if (moderationListFilter === ModerationListFilter.recentlyCreatedPosts) {
            Actions.loadRecentlyCreatedPosts(pageNumber, pageSize);
        } 
        else if(moderationListFilter === ModerationListFilter.autoRejectedPosts){
            Actions.loadAutoRejectedPosts(pageNumber, pageSize);
        }
        else {
            Actions.loadFlaggedPosts(moderationListFilter, pageNumber, pageSize);
        }
    }, [moderationListFilter, pageNumber, pageSize, signinState, refreshedAt]);

    useEffect(() => {
        // Scroll to top when page is changed
        if (previousPageNumber.current != null) {
            document.getElementById('pending-moderations-list')?.scrollIntoView();
        }
    }, [pageNumber]);

    const handlePageChange = (newPageNumber: number): void => {
        previousPageNumber.current = pageNumber;
        setPageNumber(newPageNumber);
    }

    const refreshButtonClicked = (): void => {
        setPageNumber(0);
        setRefreshedAt((new Date()).getTime());
    }

    const moderationPosts = moderationPostsResult?.data?.items ?? [];
    const totalItemsCount = moderationPostsResult.data?.totalItemsCount ?? 0;
    const maxPages = totalItemsCount / pageSize;

    const menuProps = useMemo<IContextualMenuProps>(() => ({
        shouldFocusOnMount: true,
        items: [
            {
                key: 'awaitingModeration',
                name: 'Posts awaiting moderation',
                onClick: () => {
                    setPageNumber(0);
                    setModerationListFilter(ModerationListFilter.postsAwaitingModeration);
                },
            },
            {
                key: 'moderated',
                name: 'All reported posts',
                onClick: () => {
                    setPageNumber(0);
                    setModerationListFilter(ModerationListFilter.allReportedPosts);
                },
            },
            {
                key: 'recentlyCreatedPosts',
                name: 'Posts created by external users',
                onClick: () => {
                    setPageNumber(0);
                    setModerationListFilter(ModerationListFilter.recentlyCreatedPosts);
                },
            },
            {
                key: 'autoRejectedPosts',
                name: 'Auto rejected posts',
                onClick: () => {
                    setPageNumber(0);
                    setModerationListFilter(ModerationListFilter.autoRejectedPosts);
                },
            },
        ],
    }), []);

    return (
        <Wrapper id="pending-moderations-list">
            <Flex justifyContent="space-between" alignItems="baseline" css="margin-bottom: 1.5rem">
                <div>
                    {moderationListFilter === ModerationListFilter.postsAwaitingModeration &&  'Posts awaiting moderation' }
                    {moderationListFilter === ModerationListFilter.allReportedPosts &&  'All reported posts' }
                    {moderationListFilter === ModerationListFilter.recentlyCreatedPosts &&  'Posts created by external users in the last few days' }
                    {moderationListFilter === ModerationListFilter.autoRejectedPosts &&  'Auto rejected posts' }
                    {!moderationPostsResult.isFetching && <span> - <b>{moderationPostsResult.data?.totalItemsCount ?? 0} posts</b></span>}
                </div>

                <span>
                    <RefreshButton
                        disabled={!!moderationPostsResult.isFetching}
                        onClick={() => refreshButtonClicked()}
                    />

                    <DefaultButton text="Posts awaiting moderation" menuProps={menuProps} style={{ textAlign: "left", padding: ".6rem 1.2rem", marginLeft: "2rem" }} />
                </span>
            </Flex>

            {isFetched(moderationPostsResult) && moderationPosts.length === 0 && <div>{ResourceStrings.noItemsFound()}</div>}
            {moderationPostsResult.isFetching && <Flex justifyContent="center" className="fetching">
                <Spinner size={SpinnerSize.small} />
                <span>&nbsp;&nbsp;Fetching results...</span>
            </Flex>}
            {!moderationPostsResult.isFetching && moderationPostsResult.hasErrors && <div className="error">
                There was an error in retrieving the results.
                {moderationPostsResult.errors && <pre>{stringFromErrorOrAny(moderationPostsResult.errors)}</pre>}
            </div>}

            {moderationPosts.map(x => <div key={x.contentId} css='margin-bottom: 2rem'>
                <Post post={x} filterChosen ={moderationListFilter}/>
            </div>)}

            <Pager style={{ display: isFetched(moderationPostsResult) && totalItemsCount > pageSize ? "flex" : "none", alignItems: 'baseline' }}>
                <Button
                    buttonType="link"
                    disabled={pageNumber <= 0}
                    onClick={() => handlePageChange(pageNumber - 1)}
                >
                    &lt; Previous page
                </Button>
                <span css="font-size: 1.4rem">Page {formatNumber(pageNumber + 1)} of {formatNumber(Math.ceil(totalItemsCount / pageSize))}</span>
                <Button
                    buttonType="link"
                    disabled={pageNumber >= maxPages - 1}
                    onClick={() => handlePageChange(pageNumber + 1)}
                >
                    Next page &gt;
                </Button>
            </Pager>
        </Wrapper >
    );
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 1rem;
`;

const Pager = styled.div`
    display: flex;
    justify-content: space-between;
    padding-top: 1rem;

    button {
        padding: 0 1.6rem;
        font-size: 1.6rem;
    }
`;
